import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "kakao";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 font-semibold transition-colors select-none " +
  "disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap";

const variants: Record<Variant, string> = {
  primary: "bg-primary-900 text-white hover:bg-primary-700 active:bg-primary-900",
  secondary: "bg-primary-40 text-primary-900 hover:bg-primary-80",
  outline: "border border-gray-400 bg-white text-gray-800 hover:bg-gray-200",
  ghost: "text-gray-700 hover:bg-gray-300",
  danger: "bg-negative-light text-negative hover:bg-negative-200",
  kakao: "bg-kakao text-[#000000d9] hover:brightness-95",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm rounded-sm",
  md: "h-11 px-4 text-sm rounded-md",
  lg: "h-13 px-5 text-base rounded-md",
};

export function buttonClass(variant: Variant = "primary", size: Size = "md", block = false, extra = "") {
  return [base, variants[variant], sizes[size], block ? "w-full" : "", extra].join(" ");
}

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  loading?: boolean;
  children: ReactNode;
}

export function Button({ variant = "primary", size = "md", block, loading, disabled, className = "", children, ...rest }: Props) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={buttonClass(variant, size, block, className)}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}

export function ButtonLink({
  href,
  variant = "primary",
  size = "md",
  block,
  className = "",
  children,
}: {
  href: string;
  variant?: Variant;
  size?: Size;
  block?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link href={href} className={buttonClass(variant, size, block, className)}>
      {children}
    </Link>
  );
}
